import React from "react";
import { toast } from "sonner";

/**
 * Social sign-in buttons. Google works out of the box via Emergent-managed
 * OAuth (no keys needed). Discord / Telegram / Apple are wired but need the
 * business owner to provide keys before we enable them.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
export default function SocialAuthButtons({ label = "or sign in with" }) {
  const startGoogle = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const notReady = (provider, needs) => () => {
    toast.info(`${provider} sign-in is ready to wire up — we just need ${needs}. Ask your admin to add it in Settings.`);
  };

  return (
    <div className="mt-5">
      <div className="relative">
        <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-200" /></div>
        <div className="relative flex justify-center text-[11px] uppercase tracking-widest">
          <span className="bg-white px-3 text-slate-400 font-bold">{label}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mt-4">
        {/* Google — production ready */}
        <button
          type="button"
          onClick={startGoogle}
          className="col-span-2 flex items-center justify-center gap-2 h-11 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 font-bold text-slate-900 transition shadow-sm"
          data-testid="social-google-btn"
        >
          <GoogleGlyph /> Continue with Google
        </button>

        <button
          type="button"
          onClick={notReady("Discord", "a Discord developer app + client ID/secret")}
          className="flex items-center justify-center gap-2 h-10 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-sm font-bold text-slate-700 transition"
          data-testid="social-discord-btn"
        >
          <DiscordGlyph /> Discord
        </button>
        <button
          type="button"
          onClick={notReady("Apple", "an Apple Developer Program membership + Services ID + private key")}
          className="flex items-center justify-center gap-2 h-10 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-sm font-bold text-slate-700 transition"
          data-testid="social-apple-btn"
        >
          <AppleGlyph /> Apple
        </button>
        <button
          type="button"
          onClick={notReady("Telegram", "a Telegram bot token via @BotFather")}
          className="col-span-2 flex items-center justify-center gap-2 h-10 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-sm font-bold text-slate-700 transition"
          data-testid="social-telegram-btn"
        >
          <TelegramGlyph /> Telegram
        </button>
      </div>
    </div>
  );
}

/* Brand glyphs (inline SVG so no icon lib mismatch) */
function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.9 32.7 29.4 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 8 3l5.7-5.7C33.9 6.2 29.2 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.7 18.9 12 24 12c3 0 5.8 1.1 8 3l5.7-5.7C33.9 6.2 29.2 4 24 4 16.1 4 9.2 8.5 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.3l-6.2-5.2C29.3 34.9 26.8 36 24 36c-5.4 0-9.9-3.3-11.3-8L6 32.9C8.8 39.5 15.9 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.9 2.5-2.5 4.7-4.7 6.2l6.2 5.2C40.9 36 44 30.4 44 24c0-1.2-.1-2.3-.4-3.5z" />
    </svg>
  );
}
function DiscordGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path fill="#5865F2" d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.37-.443.856-.608 1.24a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.24.078.078 0 0 0-.079-.037A19.74 19.74 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.058a.082.082 0 0 0 .031.056 19.9 19.9 0 0 0 5.993 3.03.077.077 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.995a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.331c-1.183 0-2.157-1.086-2.157-2.42 0-1.333.955-2.42 2.157-2.42 1.21 0 2.176 1.096 2.157 2.42 0 1.334-.956 2.42-2.157 2.42zm7.974 0c-1.183 0-2.157-1.086-2.157-2.42 0-1.333.955-2.42 2.157-2.42 1.21 0 2.176 1.096 2.157 2.42 0 1.334-.946 2.42-2.157 2.42z" /></svg>
  );
}
function AppleGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path fill="#000" d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" /></svg>
  );
}
function TelegramGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 240 240" aria-hidden="true"><circle cx="120" cy="120" r="120" fill="#039BE5" /><path fill="#fff" d="M53.9 116.6l114-43.9c5.3-2 9.9 1.3 8.2 9.3l-19.4 91.5c-1.4 6.8-5.5 8.5-11.1 5.3l-30.7-22.6-14.8 14.3c-1.6 1.6-3 3-6.2 3l2.2-31.4 57.2-51.7c2.5-2.2-.5-3.4-3.9-1.3l-70.6 44.5-30.4-9.5c-6.6-2-6.7-6.6 1.4-9.5z" /></svg>
  );
}
